import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';

const AccountantLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.user?.role !== 'accountant' && result.user?.role !== 'superadmin') {
        toast.error('This login is for accountant practices only. Please use the main login.');
        return;
      }
      toast.success('Welcome back!');
      navigate('/accountant/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Shield className="h-14 w-14 mx-auto mb-4 text-sky-400" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Accountant Portal</h1>
          <p className="text-slate-400 mt-1">Seafarer Logbook — Practice Access</p>
        </div>

        <Card className="bg-slate-800/90 border-slate-700 shadow-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-lg">Sign In</CardTitle>
            <CardDescription className="text-slate-400 text-sm">
              Access your practice dashboard and client data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300 text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="email" type="email" placeholder="you@practice.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-900/80 border-slate-600 text-white placeholder:text-slate-500 h-11"
                    required data-testid="accountant-login-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300 text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-slate-900/80 border-slate-600 text-white placeholder:text-slate-500 h-11"
                    required data-testid="accountant-login-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300" tabIndex={-1}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-sky-600 hover:bg-sky-700 h-11 text-base font-medium shadow-lg shadow-sky-600/20"
                data-testid="accountant-login-submit">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</> : 'Sign In'}
              </Button>
            </form>
            <p className="text-center text-slate-400 text-sm mt-6">
              Don't have a practice account?{' '}
              <Link to="/accountant/register" className="text-sky-400 hover:text-sky-300 font-medium">Register your practice</Link>
            </p>
            <p className="text-center text-slate-500 text-xs mt-3">
              <Link to="/login" className="hover:text-slate-300">Seafarer login</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AccountantLogin;
